import { apiGet, apiPost, apiPatch, apiDelete } from '@/utils/api';

export type ScheduleStatus = 'DRAFT' | 'PENDING' | 'VALIDATED' | 'PUBLISHED' | 'CANCELLED';

export interface ScheduleEntry {
  id: string;
  tenantId: string;
  academicYearId: string;
  campusId: string;
  filiereId: string;
  groupeId: string;
  moduleElementId: string;
  professorId: string;
  roomId?: string;
  sessionTypeId: string;
  dayOfWeek: number;
  startTime: string;
  endTime: string;
  isRecurring: boolean;
  recurrenceEndDate?: string;
  recurrenceWeeks?: number;
  status: ScheduleStatus;
  isCombined: boolean;
  combinedGroupIds: string[];
  /** Courses sharing this key are one physical session taught to several groups */
  sharedSessionKey?: string | null;
  /** The other courses of the shared session (empty when independent) */
  sharedWith?: SharedWithInfo[];
  createdBy: string;
  publishedBy?: string;
  publishedAt?: string;
  validatedBy?: string;
  validatedAt?: string;
  createdAt: string;
  updatedAt: string;
  // Relations
  campus?: { id: string; name: string };
  filiere?: { id: string; filiere: string };
  groupe?: { id: string; name: string };
  moduleElement?: { id: string; name: string; volumeHoraire?: number };
  professor?: { id: string; firstName: string; lastName: string };
  room?: { id: string; name: string };
  /** Per-course paid duration override (null = session type value) */
  paidDurationMinutes?: number | null;
  sessionType?: {
    id: string;
    name: string;
    durationMinutes: number;
    paidDurationMinutes?: number | null;
    ramadanBehavior?: 'KEEP_SAME' | 'SHORTEN' | null;
    ramadanDurationMinutes?: number | null;
    ramadanPaidDurationMinutes?: number | null;
    color?: string;
  };
  sessions?: ScheduleSession[];
}

/** Another course taught in the same shared session (same professor, room and slot) */
export interface SharedWithInfo {
  entryId: string;
  groupeId: string;
  groupeName: string;
  moduleElementName: string;
  filiereName: string | null;
}

export interface CreateScheduleEntryDto {
  academicYearId: string;
  campusId: string;
  filiereId: string;
  groupeId: string;
  moduleElementId: string;
  professorId: string;
  roomId?: string;
  sessionTypeId: string;
  dayOfWeek: number;
  startTime: string;
  endTime: string;
  isRecurring?: boolean;
  recurrenceEndDate?: string;
  recurrenceWeeks?: number;
  ramadanStartTime?: string;
  ramadanEndTime?: string;
  semester?: number; // For semester-aware conflict checking
  startDate?: string; // Clicked cell date (YYYY-MM-DD) — session generation starts from this week
  allowOutOfSemester?: boolean; // User explicitly confirmed scheduling outside the semester window
  confirmVolumeExceeded?: boolean; // User explicitly accepted planning beyond the subject's volume horaire
  skipConflictingDates?: boolean; // User confirmed: create the series without the weeks that clash
  paidDurationMinutes?: number; // Minutes paid to the professor per session (omit = session type value)
}

/** Outcome of realigning one course after its paid duration changed */
export interface EntryRealignment {
  entryId: string;
  moduleElementName: string;
  groupeName: string;
  removed: number;
  added: number;
  skippedReason?: string;
  conflictWarnings: string[];
}

export interface PaidDurationType {
  durationMinutes: number;
  paidDurationMinutes?: number | null;
  ramadanBehavior?: string | null;
  ramadanDurationMinutes?: number | null;
  ramadanPaidDurationMinutes?: number | null;
}

/** Paid minutes of one session: proportional to the course's paid-duration reference (Ramadan-aware) */
export function paidMinutesForSession(
  actualMinutes: number,
  sessionType: PaidDurationType | undefined,
  entryOverride?: number | null,
  inRamadan = false,
): number {
  if (!sessionType || sessionType.durationMinutes <= 0 || actualMinutes <= 0) return Math.max(0, actualMinutes);
  const regularPaid = entryOverride ?? sessionType.paidDurationMinutes ?? sessionType.durationMinutes;
  if (inRamadan && sessionType.ramadanBehavior === 'SHORTEN' && sessionType.ramadanDurationMinutes) {
    const ramadanPaid = sessionType.ramadanPaidDurationMinutes
      ?? (regularPaid * sessionType.ramadanDurationMinutes) / sessionType.durationMinutes;
    return (actualMinutes * ramadanPaid) / sessionType.ramadanDurationMinutes;
  }
  return (actualMinutes * regularPaid) / sessionType.durationMinutes;
}

/** True when an ISO date (YYYY-MM-DD…) falls inside the Ramadan period */
/**
 * Hours already planned per matière (moduleElementId), counted in PAID minutes
 * like the backend, cancelled sessions excluded. Shared by the group timetable
 * side panel and the "Planifier ici" panel.
 */
export function scheduledHoursByMatiere(entries: ScheduleEntry[], ramadanPeriod: RamadanPeriod | null | undefined): Map<string, number> {
  const hours = new Map<string, number>();
  for (const entry of entries) {
    const matiereId = entry.moduleElement?.id;
    if (!matiereId || !entry.sessions?.length) continue;
    const sessionHours = entry.sessions.reduce((total, session) => {
      if (session.isCancelled) return total;
      const [startH, startM] = session.startTime.split(':').map(Number);
      const [endH, endM] = session.endTime.split(':').map(Number);
      const durationMinutes = endH * 60 + endM - (startH * 60 + startM);
      return total + paidMinutesForSession(durationMinutes, entry.sessionType, entry.paidDurationMinutes, isDateInRamadan(session.date, ramadanPeriod)) / 60;
    }, 0);
    hours.set(matiereId, (hours.get(matiereId) || 0) + sessionHours);
  }
  return hours;
}

export function isDateInRamadan(date: string, period: RamadanPeriod | null | undefined): boolean {
  if (!period || !date) return false;
  const key = date.slice(0, 10);
  return key >= period.startDate.slice(0, 10) && key <= period.endDate.slice(0, 10);
}

export interface PartialSessionNotification {
  message: string;
  date: string;
  actualDurationMinutes: number;
  expectedDurationMinutes: number;
}

export interface ScheduleChangeResponse extends ScheduleEntry {
  partialSessionNotification?: PartialSessionNotification | null;
  adjustmentsMade?: string[];
  /** Clashes found on regenerated dates that the pre-check could not foresee */
  conflictWarnings?: string[];
  singleSessionOnly?: boolean;
}

export interface TimingChangeAdjustment {
  type: 'modified' | 'deleted' | 'added' | 'shortened' | 'extended';
  sessionDate: string;
  sessionId?: string;
  details: string;
  oldDuration?: number;
  newDuration?: number;
}

export interface TimingChangePreview {
  currentSession: {
    date: string;
    startTime: string;
    endTime: string;
    duration: number;
  };
  newTiming: {
    startTime: string;
    endTime: string;
    duration: number;
  };
  volumeHoraire: number;
  totalCurrentMinutes: number;
  durationDiff: number;
  adjustments: TimingChangeAdjustment[];
  singleSessionOnly: boolean;
}

export interface ScheduleSession {
  id: string;
  scheduleEntryId?: string;
  date: string;
  startTime: string;
  endTime: string;
  roomIdOverride?: string | null;
  professorIdOverride?: string | null;
  overrideProfessor?: {
    id: string;
    firstName: string;
    lastName: string;
  } | null;
  overrideRoom?: {
    id: string;
    name: string;
  } | null;
  isCancelled: boolean;
  cancelledBy?: string;
  cancelledAt?: string;
  cancellationReason?: string;
  isException?: boolean;
  scheduleEntry?: ScheduleEntry;
}

export interface ConflictingEntrySummary {
  id: string;
  isExam: boolean;
  isRattrapage: boolean;
  campusId: string;
  dayOfWeek: number;
  startTime: string;
  endTime: string;
  combinedGroupIds: string[];
  moduleElement: { id: string; name: string } | null;
  groupe: { id: string; name: string } | null;
  professor: { id: string; firstName: string; lastName: string } | null;
  room: { id: string; name: string } | null;
}

export interface ConflictInfo {
  type: 'PROFESSOR' | 'ROOM' | 'GROUP';
  message: string;
  /** Concrete dates (YYYY-MM-DD) on which the clash happens */
  dates: string[];
  conflictingEntryId: string;
  conflictingEntryDetails: ConflictingEntrySummary;
}

/** What the merged course will look like, shown before the user confirms */
export interface MergeTarget {
  entryId: string;
  /** Every course already part of the shared session (entryId included) */
  memberEntryIds: string[];
  moduleElementName: string;
  /** False when the new course is another subject taught together (cross-filière) */
  sameSubject: boolean;
  professorName: string;
  existingGroupNames: string[];
  existingFiliereNames: string[];
  roomName: string | null;
  roomIdToAssign: string | null;
  sessionCount: number;
  firstDate: string | null;
  lastDate: string | null;
  dayOfWeek: number;
  startTime: string;
  endTime: string;
}

/** Volume horaire situation of the subject for this group once the plan is added */
export interface VolumeCheck {
  volumeHoraire: number;
  alreadyScheduledHours: number;
  plannedHours: number;
  totalAfterHours: number;
  exceedsByHours: number;
  exceeds: boolean;
}

/** Creating the series without the clashing weeks (same bounds, nothing added at the end). */
export interface SkipOption {
  conflictingDates: string[];
  plannedCount: number;
  remainingCount: number;
  volumeAfterSkip?: VolumeCheck;
}

export interface ConflictCheckResult {
  hasConflicts: boolean;
  conflicts: ConflictInfo[];
  canCombineGroups: boolean;
  mergeTarget?: MergeTarget;
  suggestedAction?: string;
  /** Why the clashing course cannot be shared with this one */
  mergeBlockedReason?: string;
  volume?: VolumeCheck;
  skipOption?: SkipOption;
}

export type ScheduleConflictCheckDto = CreateScheduleEntryDto & { excludeEntryId?: string };

export interface ScheduleFilter {
  academicYearId?: string;
  campusId?: string;
  filiereId?: string;
  groupeId?: string;
  professorId?: string;
  status?: ScheduleStatus;
  dayOfWeek?: number;
}

export interface WeeklyViewData {
  entries: ScheduleEntry[];
  weekStart: string;
  weekEnd: string;
}

export interface BlockedDate {
  date: string;
  type: 'HOLIDAY' | 'VACATION' | 'EXAM_PERIOD';
  name: string;
  nameAr?: string;
}

export interface RamadanPeriod {
  startDate: string;
  endDate: string;
}

export interface BlockedDatesResponse {
  blockedDates: BlockedDate[];
  holidays: Array<{
    id: string;
    name: string;
    nameAr?: string;
    startDate: string;
    endDate: string;
  }>;
  vacations: Array<{
    id: string;
    name: string;
    startDate: string;
    endDate: string;
  }>;
  ramadan: RamadanPeriod | null;
}

class ScheduleService {
  // CRUD operations
  async getEntries(filter?: ScheduleFilter): Promise<ScheduleEntry[]> {
    const params = new URLSearchParams();
    if (filter?.academicYearId) params.append('academicYearId', filter.academicYearId);
    if (filter?.campusId) params.append('campusId', filter.campusId);
    if (filter?.filiereId) params.append('filiereId', filter.filiereId);
    if (filter?.groupeId) params.append('groupeId', filter.groupeId);
    if (filter?.professorId) params.append('professorId', filter.professorId);
    if (filter?.status) params.append('status', filter.status);
    if (filter?.dayOfWeek !== undefined) params.append('dayOfWeek', filter.dayOfWeek.toString());

    return apiGet<ScheduleEntry[]>(`/schedule/entries?${params.toString()}`);
  }

  async getEntry(id: string): Promise<ScheduleEntry> {
    return apiGet<ScheduleEntry>(`/schedule/entries/${id}`);
  }

  async createEntry(data: CreateScheduleEntryDto): Promise<ScheduleEntry> {
    return apiPost<ScheduleEntry>('/schedule/entries', data);
  }

  async updateEntry(id: string, data: Partial<CreateScheduleEntryDto>): Promise<ScheduleEntry> {
    return apiPatch<ScheduleEntry>(`/schedule/entries/${id}`, data);
  }

  async deleteEntry(id: string, force = false): Promise<void> {
    return apiDelete<void>(`/schedule/entries/${id}${force ? '?force=true' : ''}`);
  }

  // Conflict checking
  /** Dry-run of a creation: conflicts are evaluated on the real session dates that would be generated */
  async checkConflicts(dto: ScheduleConflictCheckDto): Promise<ConflictCheckResult> {
    return apiPost<ConflictCheckResult>('/schedule/conflicts/check', dto);
  }

  /** Change the paid duration of one course (null = back to the session type value); future sessions are realigned */
  async updatePaidDuration(entryId: string, paidDurationMinutes: number | null): Promise<EntryRealignment> {
    return apiPatch<EntryRealignment>(`/schedule/entries/${entryId}/paid-duration`, { paidDurationMinutes });
  }

  // Group operations
  /**
   * Create the new course as a member of the existing course's shared session:
   * same professor, same slot, one room, sessions on the same dates. The new
   * group keeps its own subject and attendance list.
   */
  async shareEntry(existingEntryId: string, dto: CreateScheduleEntryDto): Promise<ScheduleEntry> {
    return apiPost<ScheduleEntry>(`/schedule/entries/${existingEntryId}/share`, dto);
  }

  /** Make a course independent again (its sessions stay where they are) */
  async unshareEntry(id: string): Promise<ScheduleEntry> {
    return apiPost<ScheduleEntry>(`/schedule/entries/${id}/unshare`, {});
  }

  async combineGroups(id: string, additionalGroupIds: string[], roomId?: string): Promise<ScheduleEntry> {
    return apiPost<ScheduleEntry>(`/schedule/entries/${id}/combine-groups`, {
      groupIds: additionalGroupIds,
      ...(roomId ? { roomId } : {}),
    });
  }

  // Session operations
  async cancelSession(sessionId: string, reason: string): Promise<ScheduleSession> {
    return apiPost<ScheduleSession>(`/schedule/sessions/${sessionId}/cancel`, { reason });
  }

  // `force` = a DP/DG/BM confirmed that sessions with attendance may go too.
  async deleteSession(sessionId: string, force = false): Promise<void> {
    return apiDelete<void>(`/schedule/sessions/${sessionId}${force ? '?force=true' : ''}`);
  }

  /** Delete the sessions of a course from a date onward; earlier ones (history) stay. */
  async deleteSessionsFrom(
    entryId: string,
    fromDate: string,
    force = false,
  ): Promise<{ deleted: number; remainingSessions: number; entryDeleted: boolean }> {
    const params = new URLSearchParams({ from: fromDate });
    if (force) params.set('force', 'true');
    return apiDelete(`/schedule/entries/${entryId}/sessions?${params.toString()}`);
  }

  async rescheduleSession(
    sessionId: string,
    newDate: string,
    newStartTime: string,
    newEndTime: string,
    reason: string
  ): Promise<{ cancelledSession: ScheduleSession; newSession: ScheduleSession }> {
    return apiPost<{ cancelledSession: ScheduleSession; newSession: ScheduleSession }>(
      `/schedule/sessions/${sessionId}/reschedule`,
      { newDate, newStartTime, newEndTime, reason }
    );
  }

  async changeProfessor(entryId: string, professorId: string, fromSessionId?: string): Promise<ScheduleEntry> {
    return apiPost<ScheduleEntry>(`/schedule/entries/${entryId}/change-professor`, { professorId, fromSessionId });
  }

  async changeRoom(
    entryId: string,
    roomId: string | null,
    fromSessionId?: string,
    scope: 'current' | 'all_following' = 'all_following'
  ): Promise<ScheduleEntry> {
    return apiPost<ScheduleEntry>(`/schedule/entries/${entryId}/change-room`, { roomId, fromSessionId, scope });
  }

  async previewTimingChange(
    entryId: string,
    startTime: string,
    endTime: string,
    fromSessionId: string,
    singleSessionOnly: boolean
  ): Promise<TimingChangePreview> {
    return apiPost<TimingChangePreview>(`/schedule/entries/${entryId}/preview-timing-change`, {
      startTime,
      endTime,
      fromSessionId,
      singleSessionOnly,
    });
  }

  async changeTiming(
    entryId: string,
    dayOfWeek: number,
    startTime: string,
    endTime: string,
    fromSessionId: string,
    ramadanStartTime?: string,
    ramadanEndTime?: string,
    singleSessionOnly: boolean = false
  ): Promise<ScheduleChangeResponse> {
    return apiPost<ScheduleChangeResponse>(`/schedule/entries/${entryId}/change-timing`, {
      dayOfWeek,
      startTime,
      endTime,
      fromSessionId,
      ramadanStartTime,
      ramadanEndTime,
      singleSessionOnly,
    });
  }

  async changeSessionType(
    entryId: string,
    sessionTypeId: string,
    fromSessionId: string,
    ramadanStartTime?: string,
    ramadanEndTime?: string
  ): Promise<ScheduleChangeResponse> {
    return apiPost<ScheduleChangeResponse>(`/schedule/entries/${entryId}/change-session-type`, {
      sessionTypeId,
      fromSessionId,
      ramadanStartTime,
      ramadanEndTime,
    });
  }

  // Views
  async getWeeklyView(
    weekStart: string,
    campusId?: string,
    filiereId?: string,
    groupeId?: string
  ): Promise<WeeklyViewData> {
    const params = new URLSearchParams();
    params.append('weekStart', weekStart);
    if (campusId) params.append('campusId', campusId);
    if (filiereId) params.append('filiereId', filiereId);
    if (groupeId) params.append('groupeId', groupeId);

    return apiGet<WeeklyViewData>(`/schedule/view/weekly?${params.toString()}`);
  }

  async getProfessorSchedule(professorId: string, weekStart?: string): Promise<ScheduleEntry[]> {
    const params = new URLSearchParams();
    if (weekStart) {
      // Calculate week start (Monday) and end (Sunday)
      const startDate = new Date(weekStart);
      const endDate = new Date(startDate);
      endDate.setDate(endDate.getDate() + 6);
      params.append('startDate', startDate.toISOString().split('T')[0]);
      params.append('endDate', endDate.toISOString().split('T')[0]);
    }
    const queryString = params.toString() ? `?${params.toString()}` : '';
    return apiGet<ScheduleEntry[]>(`/schedule/view/professor/${professorId}${queryString}`);
  }

  async getStudentSchedule(startDate?: string, endDate?: string): Promise<ScheduleEntry[]> {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    const queryString = params.toString() ? `?${params.toString()}` : '';
    return apiGet<ScheduleEntry[]>(`/schedule/view/student${queryString}`);
  }

  // Blocked dates (holidays & vacations)
  async getBlockedDates(
    academicYearId: string,
    startDate: string,
    endDate: string,
    campusId?: string
  ): Promise<BlockedDatesResponse> {
    const params = new URLSearchParams();
    params.append('academicYearId', academicYearId);
    params.append('startDate', startDate);
    params.append('endDate', endDate);
    if (campusId) params.append('campusId', campusId);

    return apiGet<BlockedDatesResponse>(`/schedule/blocked-dates?${params.toString()}`);
  }

  // Check if a specific date is blocked
  isDateBlocked(blockedDates: BlockedDate[], date: string): BlockedDate | undefined {
    return blockedDates.find(b => b.date === date);
  }

  // Get canceled sessions for a date range
  async getCanceledSessions(
    groupeId: string,
    startDate: string,
    endDate: string,
    academicYearId?: string
  ): Promise<ScheduleSession[]> {
    const params = new URLSearchParams();
    params.append('groupeId', groupeId);
    params.append('startDate', startDate);
    params.append('endDate', endDate);
    params.append('isCancelled', 'true');
    if (academicYearId) params.append('academicYearId', academicYearId);

    return apiGet<ScheduleSession[]>(`/schedule/sessions?${params.toString()}`);
  }

  /**
   * Check if an entry should be displayed for a given week
   * Based on either: session records or recurrenceEndDate
   */
  shouldDisplayEntryForWeek(entry: ScheduleEntry, weekStartDate: Date, weekEndDate: Date): boolean {
    // If entry has sessions, check if any session falls within this week
    if (entry.sessions && entry.sessions.length > 0) {
      return entry.sessions.some(session => {
        const sessionDate = new Date(session.date);
        return sessionDate >= weekStartDate && sessionDate <= weekEndDate && !session.isCancelled;
      });
    }

    // If no sessions but has recurrenceEndDate, check if the week is within bounds
    if (entry.recurrenceEndDate) {
      const recurrenceEnd = new Date(entry.recurrenceEndDate);
      // Week must start before or on recurrence end
      return weekStartDate <= recurrenceEnd;
    }

    // If no sessions and no recurrenceEndDate, entry has no bounds (legacy behavior)
    // This shouldn't happen with new entries but handles old data
    return true;
  }

  /**
   * Filter entries for a specific week based on their session dates
   */
  filterEntriesForWeek(entries: ScheduleEntry[], weekStart: string): ScheduleEntry[] {
    const weekStartDate = new Date(weekStart);
    const weekEndDate = new Date(weekStartDate);
    weekEndDate.setDate(weekEndDate.getDate() + 6);

    return entries.filter(entry => this.shouldDisplayEntryForWeek(entry, weekStartDate, weekEndDate));
  }

  /**
   * Get a session for a specific date from an entry's sessions
   */
  getSessionForDate(entry: ScheduleEntry, date: Date): ScheduleSession | undefined {
    if (!entry.sessions) return undefined;
    const dateStr = date.toISOString().split('T')[0];
    return entry.sessions.find(s => {
      // Handle both string and Date formats from API
      const sessionDateStr = typeof s.date === 'string'
        ? s.date.split('T')[0]
        : new Date(s.date).toISOString().split('T')[0];
      return sessionDateStr === dateStr;
    });
  }

  /**
   * Check if there's a valid session for an entry on a specific date
   */
  hasValidSessionOnDate(entry: ScheduleEntry, date: Date): boolean {
    const session = this.getSessionForDate(entry, date);
    return session !== undefined && !session.isCancelled;
  }
}

export const scheduleService = new ScheduleService();

// Day of week labels
export const DAY_OF_WEEK_LABELS: Record<number, string> = {
  0: 'Dimanche',
  1: 'Lundi',
  2: 'Mardi',
  3: 'Mercredi',
  4: 'Jeudi',
  5: 'Vendredi',
  6: 'Samedi',
};

// Status labels
export const SCHEDULE_STATUS_LABELS: Record<ScheduleStatus, string> = {
  DRAFT: 'Brouillon',
  PENDING: 'En attente',
  VALIDATED: 'Validé',
  PUBLISHED: 'Publié',
  CANCELLED: 'Annulé',
};

// Status colors
export const SCHEDULE_STATUS_COLORS: Record<ScheduleStatus, { bg: string; text: string }> = {
  DRAFT: { bg: 'bg-gray-100', text: 'text-gray-700' },
  PENDING: { bg: 'bg-yellow-100', text: 'text-yellow-700' },
  VALIDATED: { bg: 'bg-blue-100', text: 'text-blue-700' },
  PUBLISHED: { bg: 'bg-green-100', text: 'text-green-700' },
  CANCELLED: { bg: 'bg-red-100', text: 'text-red-700' },
};
